/**
 * StreakDisplay Component
 *
 * Displays the user's current streak with fire animation.
 */

import PropTypes from 'prop-types'

const StreakDisplay = ({ streak = null, compact = false }) => {
  if (!streak || streak.currentStreak === 0) {
    return null
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1 text-orange-500">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
            clipRule="evenodd"
          />
        </svg>
        <span className="font-bold text-sm">{streak.currentStreak}</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg text-white shadow-lg">
      {/* Fire Icon with Animation */}
      <div className="relative">
        <svg className="w-6 h-6 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {/* Streak Count */}
      <div>
        <span className="font-bold text-lg">{streak.currentStreak}</span>
        <span className="text-sm ml-1">day{streak.currentStreak !== 1 ? 's' : ''}</span>
      </div>

      {/* Best Streak Indicator */}
      {streak.longestStreak > streak.currentStreak && (
        <div className="text-xs opacity-75 border-l border-white/30 pl-2 ml-1">
          Best: {streak.longestStreak}
        </div>
      )}

      {/* Active Today Indicator */}
      {streak.isActiveToday && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
      )}
    </div>
  )
}

StreakDisplay.propTypes = {
  /** Streak data object from API */
  streak: PropTypes.shape({
    currentStreak: PropTypes.number.isRequired,
    longestStreak: PropTypes.number,
    isActiveToday: PropTypes.bool
  }),
  /** If true, renders a compact version suitable for inline display */
  compact: PropTypes.bool
}

export default StreakDisplay
